import { usePage } from '@inertiajs/react';
import type { ReactNode } from 'react';

import RequiredFieldsNote from '@/components/public/forms/RequiredFieldsNote';
import Seo from '@/components/public/Seo';
import PageHero from '@/components/public/sections/PageHero';
import Section from '@/components/public/ui/Section';
import type { BreadcrumbItem } from '@/components/public/ui/Breadcrumbs';

interface ServiceRequestLayoutProps {
  seoTitle: string;
  seoDescription: string;
  badge?: string;
  breadcrumbs?: BreadcrumbItem[];
  title: string;
  accentWord?: string;
  subtitle?: string;
  /** Heading shown on the form panel. */
  panelTitle: string;
  /** Optional intro paragraph above the form. */
  panelIntro?: string;
  /** Show the "campos obrigatórios" legend above the form (off for non-form panels). */
  requiredNote?: boolean;
  /** The form (or placeholder) rendered inside the panel. */
  children: ReactNode;
}

type FlashProps = { flash: { notice?: string } };

// Shared scaffold for every service-request page: SEO + light hero + a single
// `surface-card` panel holding the form. Keeps the five request pages identical
// in structure and free of copy-pasted markup. See design-system.md §4.5.
export default function ServiceRequestLayout({
  seoTitle,
  seoDescription,
  badge,
  breadcrumbs,
  title,
  accentWord,
  subtitle,
  panelTitle,
  panelIntro,
  requiredNote = true,
  children,
}: ServiceRequestLayoutProps) {
  const { flash } = usePage<FlashProps>().props;
  const showIntro = !flash?.notice && (panelIntro || requiredNote);

  return (
    <>
      <Seo title={seoTitle} description={seoDescription} />
      <PageHero
        badge={badge}
        breadcrumbs={breadcrumbs}
        title={title}
        accentWord={accentWord}
        subtitle={subtitle}
      />
      <Section spacing="md">
        <div className="surface-card mx-auto max-w-4xl p-6 md:p-8">
          <h2 className="font-headline text-on-surface mb-2 text-2xl font-semibold">
            {panelTitle}
          </h2>
          {showIntro && (
            <div className="mb-8 space-y-2">
              {panelIntro && <p className="text-on-surface-light">{panelIntro}</p>}
              {requiredNote && <RequiredFieldsNote />}
            </div>
          )}
          {children}
        </div>
      </Section>
    </>
  );
}
